/**
 * StoreLocatorAutocompleteInput Web Component
 *
 * A reusable web component that combines Mapbox autocomplete functionality
 * with the existing store search form behavior. Supports:
 * - Real-time autocomplete suggestions
 * - Current location detection
 * - Event-driven architecture
 * - Session token management with SessionStorage persistence
 * - LocalStorage integration
 * - Session token expiration (24 hours)
 *
 * Events dispatched:
 * - 'search': When user types or searches
 * - 'location-selected': When a location is selected from suggestions
 * - 'current-location': When user requests current location
 * - 'clear': When search is cleared
 */

class StoreLocatorAutocompleteInput extends HTMLElement {
  constructor() {
    super();
    this.searchTimeout = null;
    this.searchSession = null;
    this.sessionToken = null;
    this.isMapboxLoaded = false;
    this.suppressNextAutocomplete = false; // Prevent dropdown re-opening after selection
    this.searchIcon = null;

    // Configuration
    this.config = {
      accessToken: 'pk.eyJ1IjoiYWxkb21hcGJveCIsImEiOiJjbWI5aGMxanowcjkxMmpvaHVtMDduZjRhIn0.Mpm5iAfZrFvqVr5GYd4PEQ',
      debounceDelay: 300,
      suggestionLimit: 8,
      country: window.Shopify.country || window.generalProperties.iso_code,
      language: window.locale,

      types: ['country', 'street', 'region', 'postcode', 'district', 'place', 'locality', 'neighborhood', 'address'],
    };
  }

  static get observedAttributes() {
    return ['access-token', 'placeholder', 'search-label', 'location-label', 'clear-label'];
  }

  // Get country restriction based on Shopify's current country
  getShopifyCountries() {
    try {
      // Try to get country from Shopify global object
      if (window.Shopify && window.Shopify.country) {
        const shopifyCountry = window.Shopify.country.toUpperCase();
        return [shopifyCountry];
      }

      // Fallback to common countries if Shopify country is not available
      return ['US', 'CA'];
    } catch (error) {
      // Fallback to common countries
      return ['US', 'CA'];
    }
  }

  connectedCallback() {
    this.renderFromTemplate();
    this.setupEventListeners();
    this.initializeMapboxSearch();
  }

  disconnectedCallback() {
    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    // Remove all event listeners
    this.removeAllEventListeners();

    // Remove all dropdowns
    this.removeAllDropdowns();

    // Remove all suggestions
    this.removeAllSuggestions();

    // Remove all Mapbox Search instances
    this.removeAllMapboxInstances();

    // Remove all Mapbox Search event listeners
    this.removeAllMapboxEventListeners();

    // Remove all Mapbox Search session data
    this.removeAllSessionData();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'placeholder' && oldValue !== newValue) {
      this.updateInputLabel(newValue);
    }
  }

  updateInputLabel(placeholder) {
    const form = this.querySelector(`#${this.id} .store-locator_form`);
    if (!form) return;

    const label = form.querySelector('.form-field__label');

    if (label && placeholder) {
      label.textContent = placeholder;
    }
  }

  renderFromTemplate() {
    // Use the same approach as StoreSearchForm - clone template content
    const template = this.querySelector('template');
    const formContainer = this.querySelector('.store-locator_form');

    if (template && formContainer) {
      formContainer.innerHTML = '';
      formContainer.appendChild(template.content.firstElementChild.cloneNode(true));

      this.searchIcon = formContainer.querySelector('.search-autocomplete-icon');

      const formField = formContainer.querySelector('.form-field');
      if (formField) {
        formField.style.position = 'relative';

        const dropdown = document.createElement('div');
        dropdown.className = 'store-locator-autocomplete__dropdown';
        dropdown.setAttribute('role', 'listbox');
        dropdown.setAttribute('aria-label', 'Location suggestions');

        formField.appendChild(dropdown);
      }
    }
  }

  setupEventListeners() {
    const form = this.querySelector(`#${this.id} .store-locator_form`);

    if (!form) return;

    const formField = form.querySelector('.store-locator_form-content > .form-field');
    const formFieldInput = form.querySelector('.store-locator_form-content > .form-field > input');
    const clearBtn = form.querySelector('.form-field__icon');
    const currentLocationButton = form.querySelector('.store-search-form__use-location > button');
    const locationPlaceholder = form.querySelector('.store-search-form__use-location-placeholder');
    const dropdown = this.querySelector('.store-locator-autocomplete__dropdown');

    if (!clearBtn || !currentLocationButton) {
      console.warn('StoreLocatorAutocompleteInput: No clear button or current location button found');
      return;
    }

    // Initially hide clear icon by adding context-specific class to form field
    if (formField) {
      formField.classList.add('store-search-form__clear-icon');
    }

    const hideLocationPlaceholder = () => {
      if (currentLocationButton.classList.contains('hidden')) {
        locationPlaceholder.classList.add('hidden');
        currentLocationButton.classList.remove('hidden');
        formField.classList.remove('focus');
      }
    };

    const showClearBtn = () => clearBtn.classList.remove('hidden');
    const hideClearBtn = () => clearBtn.classList.add('hidden');

    hideClearBtn();

    // Store event handler references for cleanup
    this.eventHandlers = {
      input: (e) => {
        if (e.target.value && formField) {
          showClearBtn();
        } else if (formField) {
          hideClearBtn();
        }
        this.handleSearch(e.target.value);
      },
      keypress: (e) => {
        if (e.key === 'Enter') {
          this.handleSearch(e.target.value, true);
        }
        hideLocationPlaceholder();
      },
      keydown: (e) => {
        if (e.key === 'Escape') {
          this.hideDropdown();
        }
      },
      clearBtnClick: (e) => {
        formFieldInput.value = '';
        hideLocationPlaceholder();
        // Hide clear icon by adding context-specific class to form field
        if (formField) {
          hideClearBtn();
        }
        this.hideDropdown();
        this.handleSearch('');
        this.dispatchEvent(new CustomEvent('clear'));
        formFieldInput.focus();
      },
      currentLocationClick: () => {
        formFieldInput.value = '';
        this.getUserLocation();
        formFieldInput.focus();

        locationPlaceholder.classList.remove('hidden');
        currentLocationButton.classList.add('hidden');
        // Show clear button by removing context-specific class from form field
        if (formField) {
          showClearBtn();
          formField.classList.add('focus');
        }
      },
      documentClick: (e) => {
        if (!formFieldInput.contains(e.target) && !dropdown.contains(e.target)) {
          this.hideDropdown();
        }
      },
    };

    // Add event listeners
    formFieldInput.addEventListener('input', this.eventHandlers.input);
    formFieldInput.addEventListener('keypress', this.eventHandlers.keypress);
    formFieldInput.addEventListener('keydown', this.eventHandlers.keydown);

    clearBtn.addEventListener('click', this.eventHandlers.clearBtnClick);
    // Also listen for clicks on any buttons inside the clear button area
    const clearBtnButton = clearBtn.querySelector('button');
    if (clearBtnButton) {
      clearBtnButton.addEventListener('click', this.eventHandlers.clearBtnClick);
    }

    currentLocationButton.addEventListener('click', this.eventHandlers.currentLocationClick);

    document.addEventListener('click', this.eventHandlers.documentClick);
  }

  // Generate UUID v4 for session token
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getOrGenerateSessionToken() {
    const sessionData = sessionStorage.getItem('mapbox_search_session');
    if (sessionData) {
      const parsed = JSON.parse(sessionData);

      // Check if session is still valid (24 hours)
      if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return parsed.token;
      }
      // Session expired, remove it
      sessionStorage.removeItem('mapbox_search_session');
    }

    // Generate new token and save it
    const token = this.generateUUID();
    sessionStorage.setItem(
      'mapbox_search_session',
      JSON.stringify({
        token,
        timestamp: Date.now(),
      })
    );
    return token;
  }

  // Initialize Mapbox Search
  async initializeMapboxSearch() {
    // Wait for Mapbox library to load
    if (typeof mapboxsearch === 'undefined') {
      setTimeout(() => this.initializeMapboxSearch(), 100);
      return;
    }

    try {
      if (mapboxsearch.SearchBoxCore) {
        const searchBoxCore = new mapboxsearch.SearchBoxCore({
          accessToken: this.config.accessToken,
        });

        this.searchSession = new mapboxsearch.SearchSession(searchBoxCore);
        this.isMapboxLoaded = true;

        // Store Mapbox event handler references for cleanup
        this.mapboxEventHandlers = {
          suggest: (res) => {
            this.displaySuggestions(res.suggestions || []);
          },
          retrieve: (res) => {
            if (res.features && res.features.length > 0) {
              const feature = res.features[0];
              this.handleLocationSelected(feature);
            }
          },
        };

        // Handle suggestions
        this.searchSession.addEventListener('suggest', this.mapboxEventHandlers.suggest);

        // Handle retrieval
        this.searchSession.addEventListener('retrieve', this.mapboxEventHandlers.retrieve);
      } else {
        this.sessionToken = this.getOrGenerateSessionToken();
      }
    } catch (error) {
      this.sessionToken = this.getOrGenerateSessionToken();
    }
  }

  handleSearch(query, immediate = false) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const delay = immediate ? 0 : this.config.debounceDelay;

    this.searchTimeout = setTimeout(() => {
      if (query.trim().length > 1) {
        // If we're coming from a suggestion click, suppress showing the dropdown once
        if (this.suppressNextAutocomplete) {
          this.hideDropdown();
          this.suppressNextAutocomplete = false;
        } else {
          this.performAutocomplete(query);
        }
      } else {
        this.hideDropdown();
      }

      // Dispatch search event (like original)
      this.dispatchEvent(
        new CustomEvent('search', {
          bubbles: true,
          composed: true,
          detail: {
            query,
            location: null,
          },
        })
      );
    }, delay);
  }

  // Perform autocomplete
  async performAutocomplete(query) {
    try {
      if (this.isMapboxLoaded && this.searchSession) {
        // Use SearchSession with type restrictions
        this.searchSession.suggest(query, {
          limit: this.config.suggestionLimit,
          language: this.config.language,
          country: this.config.country,
          types: this.config.types,
        });
      } else {
        // Manual implementation
        await this.performManualAutocomplete(query);
      }
    } catch (error) {
      this.hideDropdown();
    }
  }

  // Manual autocomplete (fallback)
  async performManualAutocomplete(query) {
    if (!this.sessionToken) {
      this.sessionToken = this.getOrGenerateSessionToken();
    }

    const encodedQuery = encodeURIComponent(query);
    // Include type restrictions to only return geographic locations
    const url = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodedQuery}&access_token=${
      this.config.accessToken
    }&session_token=${this.sessionToken}&limit=${this.config.suggestionLimit}&language=${
      this.config.language
    }&country=${this.config.country}&types=${this.config.types.join(',')}`;

    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    this.displaySuggestions(data.suggestions || []);
  }

  // Display suggestions
  displaySuggestions(suggestions) {
    const dropdown = this.querySelector('.store-locator-autocomplete__dropdown');
    if (!dropdown) return;

    // Get the current query from the input
    const form = this.querySelector(`#${this.id} .store-locator_form`);
    const input = form.querySelector('.store-locator_form-content > .form-field > input');
    const currentQuery = input ? input.value.toLowerCase().trim() : '';

    let dropdownHtml = '';

    suggestions.forEach((suggestion) => {
      const suggestionName = `${suggestion.name}, ${suggestion.place_formatted}`;
      const highlightedName = this.highlightQuery(suggestionName, currentQuery);

      dropdownHtml += `
        <button 
          class="store-locator-autocomplete__suggestion"
          data-suggestion='${JSON.stringify(suggestion)}'
          role="option"
          type="button"
        >
          <div class="store-locator-autocomplete__suggestion-icon">
          ${this.searchIcon.innerHTML}
          </div>

            <div class="store-locator-autocomplete__suggestion-address fixed-utility-m">${highlightedName}</div>
        </button>
      `;
    });

    dropdown.innerHTML = dropdownHtml;
    dropdown.style.display = 'block';

    // Store suggestion click handlers for cleanup
    this.suggestionClickHandlers = [];

    // Add click listeners to suggestions
    dropdown.querySelectorAll('.store-locator-autocomplete__suggestion').forEach((item) => {
      const clickHandler = () => this.selectSuggestion(item);
      this.suggestionClickHandlers.push({ element: item, handler: clickHandler });
      item.addEventListener('click', clickHandler);
    });
  }

  highlightQuery(text, query) {
    if (!query || !text) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<span class="fixed-utility-m-em">$1</span>');
  }

  // Handle suggestion selection
  async selectSuggestion(element) {
    try {
      const suggestion = JSON.parse(element.getAttribute('data-suggestion'));
      const form = this.querySelector(`#${this.id} .store-locator_form`);
      const input = form.querySelector('.store-locator_form-content > .form-field > input');
      const clearBtn = form.querySelector('.form-field__icon');

      if (!input) return;

      const mapboxId = suggestion.mapbox_id;
      // Ensure we have a session token
      if (!this.sessionToken) {
        this.sessionToken = this.getOrGenerateSessionToken();
      }

      const response = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?access_token=${this.config.accessToken}&session_token=${this.sessionToken}`
      );

      if (!response.ok) throw new Error(`Error retrieving location from Mapbox ID ${mapboxId}`);

      const data = await response.json();
      const location = {
        lat: data.features[0].geometry.coordinates[1],
        lng: data.features[0].geometry.coordinates[0],
      };

      let suggestionText = suggestion.full_address || suggestion.name || '';

      if (suggestion.context && suggestion.context.region) {
        suggestionText += `, ${suggestion.context.region.region_code}`;
      }

      this.dispatchEvent(
        new CustomEvent('search', {
          bubbles: true,
          composed: true,
          detail: {
            query: suggestionText,
            location,
          },
        })
      );

      input.value = suggestionText;
      this.suppressNextAutocomplete = true;

      clearBtn.classList.remove('hidden');
      this.hideDropdown();
    } catch (error) {
      console.warn('StoreLocatorAutocompleteInput seletSuggestion: ', error);
    }
  }

  // Handle location selected (from suggestion or retrieve)
  handleLocationSelected(feature) {
    const coordinates = feature.geometry.coordinates;
    const locationData = {
      name: feature.properties.full_address || feature.properties.name,
      coordinates: [coordinates[1], coordinates[0]], // [lat, lng]
      feature: feature,
    };

    // Dispatch location-selected event
    this.dispatchEvent(
      new CustomEvent('location-selected', {
        bubbles: true,
        composed: true,
        detail: locationData,
      })
    );
  }

  // Get user's current location (like original StoreSearchForm)
  async getUserLocation() {
    if (!navigator.geolocation) {
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      this.dispatchEvent(
        new CustomEvent('current-location', {
          bubbles: true,
          composed: true,
          detail: {
            query: `${location.lat},${location.lng}`,
            location,
          },
        })
      );

      this.dispatchEvent(
        new CustomEvent('search', {
          bubbles: true,
          composed: true,
          detail: {
            query: `${location.lat},${location.lng}`,
            location,
          },
        })
      );
    } catch (error) {
      // Handle location error silently
    }
  }

  // UI helper methods
  hideDropdown() {
    const dropdown = this.querySelector('.store-locator-autocomplete__dropdown');
    if (dropdown) {
      dropdown.style.display = 'none';
    }
  }

  setValue(value) {
    const form = this.querySelector(`#${this.id} .store-locator_form`);
    const input = form.querySelector('.store-locator_form-content > .form-field > input');
    if (input) {
      input.value = value;
      input.focus();
    }
  }

  // Cleanup methods for disconnectedCallback
  removeAllEventListeners() {
    const form = this.querySelector(`#${this.id} .store-locator_form`);
    if (!form || !this.eventHandlers) return;

    const formFieldInput = form.querySelector('.store-locator_form-content > .form-field > input');
    const clearBtn = form.querySelector('.form-field__icon');
    const currentLocationButton = form.querySelector('.store-search-form__use-location > button');
    const dropdown = this.querySelector('.store-locator-autocomplete__dropdown');

    // Remove input event listeners
    if (formFieldInput) {
      formFieldInput.removeEventListener('input', this.eventHandlers.input);
      formFieldInput.removeEventListener('keypress', this.eventHandlers.keypress);
      formFieldInput.removeEventListener('keydown', this.eventHandlers.keydown);
    }

    // Remove clear button event listeners
    if (clearBtn) {
      clearBtn.removeEventListener('click', this.eventHandlers.clearBtnClick);
      const clearBtnButton = clearBtn.querySelector('button');
      if (clearBtnButton) {
        clearBtnButton.removeEventListener('click', this.eventHandlers.clearBtnClick);
      }
    }

    // Remove current location button event listener
    if (currentLocationButton) {
      currentLocationButton.removeEventListener('click', this.eventHandlers.currentLocationClick);
    }

    // Remove document click listener
    document.removeEventListener('click', this.eventHandlers.documentClick);

    // Remove suggestion click listeners
    if (this.suggestionClickHandlers) {
      this.suggestionClickHandlers.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
      this.suggestionClickHandlers = null;
    }

    // Clear event handlers reference
    this.eventHandlers = null;
  }

  removeAllDropdowns() {
    const dropdown = this.querySelector('.store-locator-autocomplete__dropdown');
    if (dropdown) {
      dropdown.remove();
    }
  }

  removeAllSuggestions() {
    const dropdown = this.querySelector('.store-locator-autocomplete__dropdown');
    if (dropdown) {
      dropdown.innerHTML = '';
      dropdown.style.display = 'none';
    }
  }

  removeAllMapboxInstances() {
    if (this.searchSession) {
      // Clean up the search session
      this.searchSession = null;
    }
    this.isMapboxLoaded = false;
  }

  removeAllMapboxEventListeners() {
    if (this.searchSession && this.mapboxEventHandlers) {
      // Remove event listeners from search session
      this.searchSession.removeEventListener('suggest', this.mapboxEventHandlers.suggest);
      this.searchSession.removeEventListener('retrieve', this.mapboxEventHandlers.retrieve);
    }
    // Clear Mapbox event handlers reference
    this.mapboxEventHandlers = null;
  }

  removeAllSessionData() {
    // Clear session token
    this.sessionToken = null;

    // Remove session data from sessionStorage
    try {
      sessionStorage.removeItem('mapbox_search_session');
    } catch (error) {
      // Handle case where sessionStorage is not available
      console.warn('Could not clear session storage:', error);
    }
  }
}

customElements.define('store-locator-autocomplete-input', StoreLocatorAutocompleteInput);
